import React, { useMemo, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import produce from 'immer';
import { uniq } from 'lodash';

import { DatePicker, MultiSelect } from 'components';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter  } from '@cfa/react-components';
import { IReportsFilter, IStringFilter, ReportsModel } from './ReportsModel';
import { assertUnreachable } from 'services/Utils';
import { useLuxonTimezone } from 'hooks';
import { UsersService } from 'services';
import { DateTime } from 'luxon';
import { ReportsFilterType } from './enums/ReportsFilterType';

const TimePicker = DatePicker.TimePicker;

interface IReportsFilterModalProps {
    defaultFilter?: IReportsFilter,
    isNewFilter: boolean,
    model: ReportsModel,
    show: boolean,
    onCancel: () => void,
    onSave: (filter: IReportsFilter) => void,
}

interface IReportsFilterModalState {
    filter: IReportsFilter,
}

export function ReportsFilterModal({ defaultFilter, isNewFilter, model, show, onCancel, onSave }: IReportsFilterModalProps) {
    const { timezone } = useLuxonTimezone(UsersService.me.timezone);

    const table = model.allTablesById[model.activeTableId];

    const [state, setState] = useState<IReportsFilterModalState>(() => {
        const out = {filter: undefined, textareaVal: ''};
        if (defaultFilter) {
            // Copy the filter so that we don't modify it until we save.
            let newDefaultFilter: IReportsFilter = cloneDeep(defaultFilter);

            // If the filter uses "in", we need to join the values array for the textarea
            let joinedVal = '';
            if (
                defaultFilter.type === ReportsFilterType.String
                && defaultFilter.comparator === 'in'
                && Array.isArray(defaultFilter.value)
            ) {
                joinedVal = defaultFilter.value.join(',\n');
            }
            
            return {
                filter: newDefaultFilter,
                textareaVal: joinedVal,
            };
        }

        // We weren't passed a filter, so default to the first field of the active table.
        return {
            filter: model.createBlankFilter(table.fields[0]),
        };
    })
    const { filter } = state;

    const multiSelectOptions: string[] = useMemo(() => {
        if (filter.type === ReportsFilterType.String && filter.comparator === 'in') {
            const field = model.outFieldsById[filter.fieldId];
            if (!field) return [];

            // Pull the field value from all rows. This way, we include values from rows that aren't in our filtered output.
            const allRows = model.allRowsByTableId?.[model.activeTableId];
            if (!allRows) return [];

            const values = allRows.map(row => row[field.propName] + '');

            // Unique the values and sort ABC ascending.
            const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
            return uniq(values).sort(collator.compare);
        }
        return [];
    }, [filter.fieldId, filter.type, filter.comparator, model.activeTableId, model.outRows]);

    // Ant pickers don't support timezones, so we need to convert to browser time.
    // We'll convert back in the onChange handler.
    let appliedTimezoneDate: Date;
    if (filter.type === ReportsFilterType.Date) {
        appliedTimezoneDate = filter.value.setZone('local', {keepLocalTime: true}).toJSDate();
    }

    function onChangeFieldId(e) {
        const fieldId = e.target.value;
        setState(produce(draft => {
            const field = model.allFieldsById[fieldId];
            if (!field) {
                console.error('Missing field for id: ', fieldId);
                return;
            }

            // Replace the filter with a default one, but we need to keep the id so that we know it's the same filter.
            const oldFilterId = draft.filter.id;
            draft.filter = model.createBlankFilter(field);
            draft.filter.id = oldFilterId;
        }))
    }

    function onChangeComparator(e) {
        const newComparator = e.target.value;
        const oldComparator = filter.comparator;

        setState(produce(draft => {
            draft.filter.comparator = newComparator;
            // If changing to or from a list, we need to reset the value to the right type.
            switch (draft.filter.type) {
                case ReportsFilterType.String:
                    if (draft.filter.comparator === 'in') {
                        draft.filter.value = [];
                    } else if (oldComparator === 'in') {
                        draft.filter.value = '';
                    }
                    break;
                case ReportsFilterType.Number:
                case ReportsFilterType.Date:
                    break;
                default:
                    assertUnreachable(draft.filter);
            }
        }));
    }

    function onChangeNot(e) {
        const isNegated = e.target.value === 'not';
        setState(produce(draft => {
            draft.filter = draft.filter as IStringFilter;
            draft.filter.negateComparison = isNegated;
        }));
    }

    function onChangeValueString(e) {
        const val = e.target.value;
        setState(produce(draft => {
            draft.filter.value = val;
        }));
    }

    function onChangeValueNumber(e) {
        const val = Number(e.target.value);
        setState(produce(draft => {
            draft.filter.value = val;
        }));
    }

    function onChangeValueStringArray(newValue: string[]) {
        setState(produce(draft => {
            draft.filter.value = newValue;
        }));
    }

    function onChangeDate(newDate: Date) {
        setState(produce(draft => {
            // AntD pickers don't support timezones, so we need to convert from local time.
            const newDateMapped = DateTime
                .fromJSDate(newDate)
                .setZone(timezone, {keepLocalTime: true});
            draft.filter.value = newDateMapped;
        }));
    }

    function onClickSave() {
        onSave(filter);
    }

    // If we have aggregate fields like "(Total)", we need to add them in.
    const allFields = table.fields.concat(model.outFields.filter(f => f.isGeneratedAggregate));

    return (
        <Modal show={show} onClose={onCancel}>
            <ModalHeader>
                {isNewFilter && 'Add Filter'}
                {!isNewFilter && 'Edit Filter'}
            </ModalHeader>
            <ModalBody>
                <div className="row" style={{'marginTop': '0px'}}>
                    <div className="col">
                        <div className="formItem">
                            <div className="formItem_label">
                                Field
                            </div>
                            <select
                                className="formItem_select"
                                value={filter.fieldId}
                                onChange={onChangeFieldId}
                            >
                                {allFields.map(f => (
                                    <option key={f.id} value={f.id}>
                                        {f.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <div className="formItem">
                            <div className="formItem_label">
                                Compare Function
                            </div>
                            {filter.type === ReportsFilterType.String && (
                                <div className="reportsModal_multiInputWrap">
                                    <select
                                        className="formItem_select reportsModal_multiInputNot"
                                        value={filter.negateComparison ? 'not' : ''}
                                        onChange={onChangeNot}
                                    >
                                        <option value=""></option>
                                        <option value="not">Not</option>
                                    </select>
                                    <select
                                        className="formItem_select reportsModal_multiInputValue"
                                        value={filter.comparator}
                                        onChange={onChangeComparator}
                                    >
                                        <option value="in">One Of</option>
                                        <option value="contains">Contains</option>
                                        <option value="startsWith">Starts With</option>
                                        <option value="equal">Equals</option>
                                    </select>
                                </div>
                            )}
                            {filter.type === ReportsFilterType.Date && (
                                <select
                                    className="formItem_select"
                                    value={filter.comparator}
                                    onChange={onChangeComparator}
                                >
                                    <option value="<">Before</option>
                                    <option value=">">After</option>
                                </select>
                            )}
                            {filter.type === ReportsFilterType.Number && (
                                <select
                                    className="formItem_select"
                                    value={filter.comparator}
                                    onChange={onChangeComparator}
                                >
                                    <option value="<">&lt;</option>
                                    <option value="<=">&lt;=</option>
                                    <option value=">">&gt;</option>
                                    <option value=">=">&gt;=</option>
                                    <option value="=">=</option>
                                </select>
                            )}
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <div className="formItem">
                            <div className="formItem_label">
                                Value
                            </div>
                            {filter.type === ReportsFilterType.String && (<>
                                {(
                                    filter.comparator === 'contains' 
                                    || filter.comparator === 'equal'
                                    || filter.comparator === 'startsWith'
                                ) && (<>
                                    <div className="formItem_label">
                                        Value
                                    </div>
                                    <input
                                        className="formItem_input"
                                        type="text"
                                        value={filter.value}
                                        onChange={onChangeValueString}
                                    />
                                </>)}
                                {(filter.comparator === 'in') && (<>
                                    <MultiSelect<string>
                                        onPaste={(event) => {
                                            event.preventDefault();
                                            const pasted = event.clipboardData.getData('Text');
                                            const locs = pasted.split(/,\s*|\s+/);
                                            onChangeValueStringArray(locs);
                                        }}
                                        value={filter.value as string[]}
                                        getOptionId={option => option}
                                        getOptionSearchValue={option => option}
                                        onChange={onChangeValueStringArray}
                                        options={multiSelectOptions}
                                    />
                                </>)}
                            </>)}
                            {filter.type === ReportsFilterType.Date && (<>
                                <DatePicker
                                    getPopupContainer={(trig) => trig.parentNode as HTMLElement}
                                    value={appliedTimezoneDate}
                                    onChange={onChangeDate}
                                    picker="date"
                                    inputReadOnly={true}
                                    suffixIcon={null}
                                    allowClear={false}
                                />
                                <TimePicker
                                    getPopupContainer={(trig) => trig.parentNode as HTMLElement}
                                    className="dateTimeRange timeRange"
                                    value={appliedTimezoneDate}
                                    format={'h:mm:ss a'}
                                    showNow={false}
                                    allowClear={false}
                                    inputReadOnly={true}
                                    onSelect={onChangeDate}
                                />
                            </>)}
                            {filter.type === ReportsFilterType.Number && (<>
                                <input
                                    className="formItem_input"
                                    type="text"
                                    value={filter.value}
                                    onChange={onChangeValueNumber}
                                />
                            </>)}
                        </div>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button
                    variant="outlined"
                    onClick={onCancel}
                >
                    Cancel
                </Button>
                <Button
                    onClick={onClickSave}
                >
                    Save
                </Button>
            </ModalFooter>
        </Modal>
    );
}